import { useState } from 'react';
import { Check, Copy } from 'lucide-react';

export function Badge({ children, color = 'accent' }) {
  const colors = {
    accent: { bg: 'var(--accent-dim)', color: 'var(--accent)' },
    amber: { bg: 'var(--amber-dim)', color: 'var(--amber)' },
    red: { bg: 'var(--red-dim)', color: 'var(--red)' },
    blue: { bg: 'var(--blue-dim)', color: 'var(--blue)' },
    text: { bg: 'var(--border)', color: 'var(--text2)' },
  };
  const c = colors[color] || colors.accent;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      background: c.bg, color: c.color,
      fontSize: 11, fontWeight: 500, padding: '3px 9px',
      borderRadius: 20, fontFamily: 'var(--font-mono)', letterSpacing: '0.02em',
    }}>{children}</span>
  );
}

export function Card({ children, style = {}, onClick }) {
  return (
    <div onClick={onClick} style={{
      background: 'var(--surface)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius-lg)', padding: '1.25rem',
      cursor: onClick ? 'pointer' : 'default',
      transition: 'border-color 0.15s',
      ...style,
    }}
    onMouseEnter={e => onClick && (e.currentTarget.style.borderColor = 'var(--border2)')}
    onMouseLeave={e => onClick && (e.currentTarget.style.borderColor = 'var(--border)')}
    >{children}</div>
  );
}

export function CopyButton({ text, style = {} }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  return (
    <button onClick={copy} style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      fontSize: 12, padding: '5px 12px',
      background: copied ? 'var(--accent-dim)' : 'var(--surface2)',
      color: copied ? 'var(--accent)' : 'var(--text2)',
      border: '1px solid var(--border2)',
      borderRadius: 'var(--radius)', transition: 'all 0.15s',
      fontFamily: 'var(--font-mono)',
      ...style,
    }}>
      {copied ? <Check size={13} /> : <Copy size={13} />}
      {copied ? 'Copied!' : 'Copy'}
    </button>
  );
}

export function StatCard({ label, value, sub, color }) {
  return (
    <div style={{
      background: 'var(--surface)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius-lg)', padding: '1.1rem 1.25rem',
    }}>
      <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 30, fontWeight: 700, fontFamily: 'var(--font-display)', color: color || 'var(--text)', lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 6 }}>{sub}</div>}
    </div>
  );
}

export function Input({ placeholder, value, onChange, style = {} }) {
  return (
    <input
      value={value} onChange={onChange} placeholder={placeholder}
      style={{
        background: 'var(--bg2)', border: '1px solid var(--border2)',
        borderRadius: 'var(--radius)', padding: '9px 14px',
        color: 'var(--text)', fontSize: 14, outline: 'none',
        transition: 'border-color 0.15s',
        ...style,
      }}
      onFocus={e => e.target.style.borderColor = 'var(--accent)'}
      onBlur={e => e.target.style.borderColor = 'var(--border2)'}
    />
  );
}

export function Select({ value, onChange, children, style = {} }) {
  return (
    <select value={value} onChange={onChange} style={{
      background: 'var(--bg2)', border: '1px solid var(--border2)',
      borderRadius: 'var(--radius)', padding: '9px 14px',
      color: 'var(--text)', fontSize: 14, outline: 'none', cursor: 'pointer',
      ...style,
    }}>{children}</select>
  );
}

export function Btn({ children, onClick, variant = 'primary', style = {} }) {
  const variants = {
    primary: { background: 'var(--accent)', color: '#000', border: 'none', fontWeight: 600 },
    ghost: { background: 'transparent', color: 'var(--text2)', border: '1px solid var(--border2)', fontWeight: 400 },
    danger: { background: 'var(--red-dim)', color: 'var(--red)', border: '1px solid var(--red-dim)', fontWeight: 500 },
  };
  return (
    <button onClick={onClick} style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '9px 18px', borderRadius: 'var(--radius)',
      fontSize: 14, transition: 'opacity 0.15s, transform 0.1s',
      ...variants[variant], ...style,
    }}
    onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
    onMouseLeave={e => e.currentTarget.style.opacity = '1'}
    onMouseDown={e => e.currentTarget.style.transform = 'scale(0.97)'}
    onMouseUp={e => e.currentTarget.style.transform = 'scale(1)'}
    >{children}</button>
  );
}

export function SectionLabel({ children }) {
  return (
    <div style={{
      fontSize: 10, fontWeight: 600, letterSpacing: '0.12em',
      textTransform: 'uppercase', color: 'var(--text3)',
      fontFamily: 'var(--font-mono)', marginBottom: 12,
    }}>{children}</div>
  );
}
